import i18n from 'src/lib/i18n/i18n';
import View from '@play-co/timestep-core/lib/ui/View';
import { Feature } from 'src/replicant/analytics';
import PopupMatch2Base, { PopupMatch2BaseInitOptions } from './PopupMatch2Base';
import StateObserver from 'src/StateObserver';
import Match2Mascots from './Match2Mascots';
import ShineFx from './ShineFx';
import ConfettiOverlay from './ConfettiOverlay';
import { Match2RewardsPanel } from './Match2RewardsPanel';
import { ImageScaleView } from '@play-co/timestep-core/ui';
import { gsap } from 'gsap';
import Match2PopupSubHeader from './Match2PopupSubHeader';
import { getDailyBonusRewards } from 'src/replicant/match2/match2.getters';

export default class PopupMatch2DailyBonus extends PopupMatch2Base {
  private confetti: ConfettiOverlay;
  private backView!: View;
  private shineFx!: ShineFx;
  private mascots!: Match2Mascots;
  private giftBox!: ImageScaleView;
  private rewards: Match2RewardsPanel;

  constructor(opts: { superview: View; close: () => void }) {
    super({
      superview: opts.superview,
      title: {
        type: 'banner',
        localeText: () => i18n('match2.dailyBonus.title').toLocaleUpperCase(),
        color: 'red',
        textSize: 42,
        textOffsetY: 25,
      },
      cta: {
        localeText: () => i18n('match2.dailyBonus.claim'),
        positioning: 'inside',
        width: 300,
        onClick: async () => {
          this.setInteractivity(false);
          await this.grantRewards();
          opts.close();
        },
      },
      tracking: {
        feature: Feature.MATCH2.DAILY_BONUS._,
        $subFeature: Feature.MATCH2.DAILY_BONUS.COLLECT,
      },
    });

    this.confetti = new ConfettiOverlay({ superview: this.root });
    this.createBackView();

    new Match2PopupSubHeader({
      superview: this.panel,
      x: this.panel.style.width * 0.5,
      y: 105,
      localeText: () => i18n('match2.dailyBonus.rewards'),
    });

    this.rewards = new Match2RewardsPanel({
      superview: this.panel,
      x: this.panel.style.width * 0.5,
      y: this.panel.style.height * 0.5 + 5,
    });
  }

  private createBackView() {
    this.backView = new View({
      superview: this.root,
      centerOnOrigin: true,
      centerAnchor: true,
      x: this.panelView.style.x,
      y: this.panelView.style.y - this.panelView.style.height * 0.5,
      clipEvents: false,
      zIndex: 1,
    });

    this.shineFx = new ShineFx({ superview: this.backView });
    this.mascots = new Match2Mascots({
      superview: this.backView,
      config: [
        {
          image: 'assets/ui/match2/common/mascot_1.png',
          width: 155,
          height: 176,
          x: -165,
          y: -90,
        },
        {
          image: 'assets/ui/match2/common/mascot_2.png',
          width: 166,
          height: 192,
          x: 155,
          y: -90,
        },
      ],
    });

    this.giftBox = new ImageScaleView({
      superview: this.backView,
      image: 'assets/ui/match2/daily_bonus_box.png',
      width: 184,
      height: 205,
      centerOnOrigin: true,
      centerAnchor: true,
      y: 50,
    });

    this.backView.hide();
  }

  public async init(opts: PopupMatch2BaseInitOptions) {
    super.init(opts);
    const rewards = getDailyBonusRewards(StateObserver.getState().user);
    this.rewards.setRewards(rewards);
  }

  public override async fadeIn() {
    this.confetti.show();
    await super.fadeIn(false);
    this.backView.show();
    gsap.to(this.giftBox.style, { duration: 0.5, y: -110, ease: 'back.out' });
    this.shineFx.show();
    await this.mascots.show();
    this.setInteractivity(true);
  }

  public override async fadeOut() {
    this.confetti.hide();
    this.shineFx.hide();
    this.mascots.hide();
    this.backView.hide();
    super.fadeOut();
  }

  private async grantRewards() {
    await this.rewards.grantRewards(this.root);
    await StateObserver.invoke.collectDailyBonusRewards();
  }
}
